<template>
  <DModal
    :show-close="false"
    :isBgOverlay="true"
    :appendToBody="true"
    @close="$emit('close')">
    <div class="organisations-modal text-dark"
         :class="{'organisations-modal--full': isFullSize}"
         slot="insertComponent"
         @click.stop>
      <div class="organisations-modal__header">
        <h2>Organisationer</h2>
        <div class="font-large icon__bg-circle organisations-modal__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="organisations-modal__tabs">
        <Tabs
          :tabs="theTabs"
          :selected="activeTab"
          :isLarge="true"
          @select="setActiveTab">
        </Tabs>
      </div>

      <!-- Step 1: vælg organisationer -->
      <template v-if="activeTab === 'select'">
        <div class="organisations-modal__content">
          <p class="organisations-modal__explainer">
            Vælg de organisationer, der skal hentes ansættelser ud fra.
          </p>
          <Loader bgColor="#fff" v-if="isLoading"></Loader>
          <template v-else>
            <p class="italic" v-if="organisations.length === 0">
              Der blev ikke fundet nogen organisationer.
            </p>
            <ul class="organisations-modal__list" v-else>
              <li v-for="organisation in organisations"
                  :key="organisation"
                  class="organisations-modal__list__item"
                  :class="{'organisations-modal__list__item--active': isSelected(organisation)}"
                  @click="toggleOrganisation(organisation)">
                <div class="organisations-modal__list__item__name">
                  <span class="icon-home2"></span>
                  <div>
                    {{ organisation }}
                    <div class="organisations-modal__list__item__counts">
                      {{ organisationCounts(organisation).total }}
                      {{ organisationCounts(organisation).total === 1 ? 'ansættelse' : 'ansættelser' }},
                      heraf {{ organisationCounts(organisation).withTemplate }} med stillingskategori
                    </div>
                  </div>
                </div>
                <div class="icon__checkmark" v-if="isSelected(organisation)">
                  <span class="icon-checkmark-circle"></span>
                </div>
              </li>
            </ul>
          </template>
        </div>

        <div class="organisations-modal__buttons">
          <span class="organisations-modal__count">
            Valgt {{ selectedOrganisations.length }} af {{ organisations.length }}
          </span>
          <div>
            <button class="do-bttn" @click="$emit('close')">
              {{ $t('vue2.common.cancel') }}
            </button>
            <button
              class="do-bttn do-bttn--submit"
              :class="{
                'do-bttn--submit--disabled': isLoading || isSaving,
                'do-bttn--submit--loading': isSaving
              }"
              @click="save">
              Gem
            </button>
          </div>
        </div>
      </template>

      <!-- Step 2: overførsler uden for de valgte organisationer -->
      <template v-if="activeTab === 'outside'">
        <div class="organisations-modal__content organisations-modal__content--full">
          <p class="organisations-modal__explainer">
            Løn- og fraværsoverførsler på ansættelser, hvis organisation ikke er blandt de valgte.
            Brug listen til at finde overførsler, der ved en fejl er lavet på en forkert organisation.
          </p>
          <p class="organisations-modal__warning" v-if="outsideData && outsideData.salary_organisations.length === 0">
            Der er ikke valgt nogen organisationer endnu, så alle ansættelser vises.
            Vælg organisationer under "1. Vælg organisationer".
          </p>
          <Loader bgColor="#fff" v-if="isLoadingOutside"></Loader>
          <template v-else-if="outsideData">
            <div class="organisations-modal__outside-summary">
              <span>
                {{ outsideEmployments.length }}
                {{ outsideEmployments.length === 1 ? 'ansættelse' : 'ansættelser' }}
                uden for de valgte organisationer, heraf
                {{ employmentsWithTransfers.length }} med overførsler
              </span>
              <label class="organisations-modal__outside-toggle" v-if="outsideEmployments.length > employmentsWithTransfers.length">
                <input type="checkbox" v-model="isShowAllOutside">
                Vis også ansættelser uden overførsler
              </label>
            </div>

            <p class="italic" v-if="shownEmployments.length === 0">
              Ingen overførsler fundet uden for de valgte organisationer.
            </p>

            <div class="outside-employment" v-for="employment in shownEmployments" :key="employment.employment_id">
              <div class="outside-employment__header">
                <div class="outside-employment__header__who">
                  <strong>{{ employment.user_name }}</strong>
                  <span class="outside-employment__header__number">
                    {{ employment.employment_number }}<template v-if="employment.source_title"> - {{ employment.source_title }}</template>
                  </span>
                  <div class="d-tag" v-for="position in employment.positions" :key="position.school_employment_id" v-if="position.title">
                    {{ position.title }}
                  </div>
                </div>
                <div class="do-pill do-pill--small do-pill--yellow">
                  {{ employment.organisation || 'Ingen organisation' }}
                </div>
              </div>

              <div class="outside-employment__section" v-if="employment.v_salary_days.length > 0">
                <h4>Vikarløn - dage</h4>
                <table class="outside-table">
                  <thead>
                  <tr>
                    <th>Dato</th>
                    <th>Navn</th>
                    <th>Tid på ansættelsen</th>
                    <th>Status</th>
                    <th>Godkendt</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="day in employment.v_salary_days" :key="'day-' + day.id">
                    <td>{{ formatDate(day.day) }}</td>
                    <td>{{ day.name }}</td>
                    <td>{{ formatMinutes(day.v_minutes) }}</td>
                    <td>{{ day.state }}</td>
                    <td>{{ formatDate(day.approved_at) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="outside-employment__section" v-if="employment.v_kmd_rows.length > 0">
                <h4>Engangsydelser - OPUS</h4>
                <table class="outside-table">
                  <thead>
                  <tr>
                    <th>Dato</th>
                    <th>Lønart</th>
                    <th>Antal</th>
                    <th>Beløb</th>
                    <th>Omk.sted</th>
                    <th>Status</th>
                    <th>Sendt</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(row, idx) in employment.v_kmd_rows" :key="'kmd-' + idx">
                    <td>{{ formatDate(row.Startdato) }}</td>
                    <td>{{ row.Loenart }}</td>
                    <td>{{ row.Antal }}</td>
                    <td>{{ row.Beloeb }}</td>
                    <td>{{ row.Afv_Omkostningssted || '—' }}</td>
                    <td>{{ row.v_status }}</td>
                    <td>{{ formatDate(row.v_created_at) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="outside-employment__section" v-if="employment.sd_engangsydelser.length > 0">
                <h4>Engangsydelser - SD</h4>
                <table class="outside-table">
                  <thead>
                  <tr>
                    <th>Dato</th>
                    <th>Lønart</th>
                    <th>Enheder</th>
                    <th>Sats</th>
                    <th>Dage</th>
                    <th>Timer</th>
                    <th>Beskrivelse</th>
                    <th>Status</th>
                    <th>Godkendt</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="ydelse in employment.sd_engangsydelser" :key="'sdy-' + ydelse.id">
                    <td>{{ formatSdDate(ydelse) }}</td>
                    <td>{{ ydelse.loenart }}</td>
                    <td>{{ ydelse.enheder }}</td>
                    <td>{{ ydelse.sats }}</td>
                    <td>{{ ydelse.dage }}</td>
                    <td>{{ ydelse.timer }}</td>
                    <td>{{ ydelse.description }}</td>
                    <td>{{ ydelse.state }}</td>
                    <td>{{ formatDate(ydelse.approved_at) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="outside-employment__section" v-if="employment.sd_fravaer.length > 0">
                <h4>Fravær - SD</h4>
                <table class="outside-table">
                  <thead>
                  <tr>
                    <th>Periode</th>
                    <th>Lønart</th>
                    <th>Dage</th>
                    <th>Status</th>
                    <th>Godkendt</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="fravaer in employment.sd_fravaer" :key="'sdf-' + fravaer.id">
                    <td>{{ formatPeriod(fravaer.startdato, fravaer.slutdato) }}</td>
                    <td>{{ fravaer.loenart }}</td>
                    <td>{{ fravaer.dagdecimal }}</td>
                    <td>{{ fravaer.state }}</td>
                    <td>{{ formatDate(fravaer.approved_at) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="outside-employment__section" v-if="employment.sd_loenaendringer.length > 0">
                <h4>Lønændringer - SD</h4>
                <table class="outside-table">
                  <thead>
                  <tr>
                    <th>Dato</th>
                    <th>Deltidskode</th>
                    <th>Løndecimal</th>
                    <th>Beskæftigelsesdecimal</th>
                    <th>Årstimetal</th>
                    <th>Status</th>
                    <th>Godkendt</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="aendring in employment.sd_loenaendringer" :key="'sdl-' + aendring.id">
                    <td>{{ formatDate(aendring.dato) }}</td>
                    <td>{{ aendring.deltidskode }}</td>
                    <td>{{ aendring.loendecimal }}</td>
                    <td>{{ aendring.beskaeftigelsesdecimal }}</td>
                    <td>{{ aendring.aarstimetal }}</td>
                    <td>{{ aendring.state }}</td>
                    <td>{{ formatDate(aendring.approved_at) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="outside-employment__section" v-if="employment.sd_tillaeg.length > 0">
                <h4>Tillæg - SD</h4>
                <table class="outside-table">
                  <thead>
                  <tr>
                    <th>Dato</th>
                    <th>Ændringskode</th>
                    <th>Tillægsnummer</th>
                    <th>Enheder</th>
                    <th>Status</th>
                    <th>Godkendt</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="tillaeg in employment.sd_tillaeg" :key="'sdt-' + tillaeg.id">
                    <td>{{ formatDate(tillaeg.dato) }}</td>
                    <td>{{ tillaeg.aendringskode }}</td>
                    <td>{{ tillaeg.tillaegsnummer }}</td>
                    <td>{{ tillaeg.enheder }}</td>
                    <td>{{ tillaeg.state }}</td>
                    <td>{{ formatDate(tillaeg.approved_at) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="outside-employment__section" v-if="employment.salary_supplements.length > 0">
                <h4>Ydelser og tillæg</h4>
                <table class="outside-table">
                  <thead>
                  <tr>
                    <th>Lønkode</th>
                    <th>Skoleår</th>
                    <th>Enheder</th>
                    <th>Rater</th>
                    <th>Periode</th>
                    <th>Status</th>
                    <th>Godkendt</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="supplement in employment.salary_supplements" :key="'sup-' + supplement.id">
                    <td>{{ supplement.code }}</td>
                    <td>{{ supplement.school_year }}</td>
                    <td>{{ supplement.units }}</td>
                    <td>{{ supplement.installments }}</td>
                    <td>{{ formatPeriod(supplement.valid_from, supplement.valid_to) }}</td>
                    <td>{{ supplement.state }}</td>
                    <td>{{ formatDate(supplement.approved_at) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="outside-employment__section" v-if="employment.absences.length > 0">
                <h4>Fravær</h4>
                <table class="outside-table">
                  <thead>
                  <tr>
                    <th>Årsag</th>
                    <th>Periode</th>
                    <th>Tid</th>
                    <th>Fraværskode</th>
                    <th>Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="absence in employment.absences" :key="'abs-' + absence.event_serial_id">
                    <td>{{ absence.subject_name }}</td>
                    <td>
                      {{ formatDate(absence.from_date) }}
                      <template v-if="absence.ongoing">- løbende</template>
                      <template v-else-if="absence.to_date && absence.to_date !== absence.from_date">- {{ formatDate(absence.to_date) }}</template>
                    </td>
                    <td>
                      <template v-if="absence.use_time">{{ absence.from_time }} - {{ absence.to_time }}</template>
                    </td>
                    <td>{{ absence.kmd_absence_code || absence.sd_absence_code }}</td>
                    <td>{{ absence.state }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <p class="italic organisations-modal__no-transfers" v-if="transfersCount(employment) === 0">
                Ingen overførsler på denne ansættelse.
              </p>
            </div>
          </template>
        </div>

        <div class="organisations-modal__buttons">
          <span></span>
          <div>
            <button class="do-bttn" @click="$emit('close')">
              Luk
            </button>
          </div>
        </div>
      </template>

      <!-- Step 3: ubrugte ansættelser uden for de valgte organisationer -->
      <template v-if="activeTab === 'unused'">
        <div class="organisations-modal__content organisations-modal__content--full">
          <p class="organisations-modal__explainer">
            Ansættelser uden for de valgte organisationer, som intet er bogført på -
            hverken vikarløn, ydelser eller fravær. De kan slettes, uden at skolen mister noget.
          </p>
          <p class="organisations-modal__warning" v-if="isUnusedLoaded && school && Array.isArray(school.salary_organisations) && school.salary_organisations.length === 0">
            Der er ikke valgt nogen organisationer endnu, så alle ubrugte ansættelser vises.
            Vælg organisationer under "1. Vælg organisationer".
          </p>
          <Loader bgColor="#fff" v-if="isLoadingUnused"></Loader>
          <template v-else-if="isUnusedLoaded">
            <p class="italic" v-if="unusedEmployments.length === 0">
              Ingen ubrugte ansættelser uden for de valgte organisationer.
            </p>
            <table class="outside-table outside-table--boxed" v-else>
              <thead>
              <tr>
                <th>Medarbejder</th>
                <th>Ansættelse</th>
                <th>Titel</th>
                <th>Organisation</th>
                <th>Periode</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="position in unusedEmployments" :key="'unused-' + position.id">
                <td>{{ userName(position.user_id) }}</td>
                <td>
                  {{ position.employment_number }}<template v-if="position.source_title"> - {{ position.source_title }}</template>
                </td>
                <td>{{ position.title }}</td>
                <td>{{ position.organisation || 'Ingen organisation' }}</td>
                <td>{{ formatPeriod(position.start_date, position.end_date) }}</td>
              </tr>
              </tbody>
            </table>
          </template>
        </div>

        <div class="organisations-modal__buttons">
          <span class="organisations-modal__count" v-if="isConfirmingUnusedDelete">
            Er du sikker? {{ unusedEmployments.length }}
            {{ unusedEmployments.length === 1 ? 'ansættelse markeres' : 'ansættelser markeres' }} som slettet.
          </span>
          <span v-else></span>
          <div>
            <template v-if="isConfirmingUnusedDelete">
              <button class="do-bttn" :disabled="isDeletingUnused" @click="isConfirmingUnusedDelete = false">
                Fortryd
              </button>
              <button
                class="do-bttn do-bttn--submit"
                :class="{
                  'do-bttn--submit--disabled': isDeletingUnused,
                  'do-bttn--submit--loading': isDeletingUnused
                }"
                @click="deleteUnused">
                Ja, slet {{ unusedEmployments.length }}
                {{ unusedEmployments.length === 1 ? 'ansættelse' : 'ansættelser' }}
              </button>
            </template>
            <template v-else>
              <button class="do-bttn" @click="$emit('close')">
                Luk
              </button>
              <button
                class="do-bttn do-bttn--submit"
                v-if="unusedEmployments.length > 0"
                :class="{'do-bttn--submit--disabled': isLoadingUnused}"
                @click="isConfirmingUnusedDelete = true">
                Slet {{ unusedEmployments.length }} ubrugte
                {{ unusedEmployments.length === 1 ? 'ansættelse' : 'ansættelser' }}
              </button>
            </template>
          </div>
        </div>
      </template>
    </div>
  </DModal>
</template>

<script>
import DModal from '../../common/DModal.vue'
import Loader from '../../common/Loader.vue'
import Tabs from '../../common/Tabs.vue'
import docendo from '../../../docendo'
import eventbus from '../../../eventbus'
import moment from 'moment'

export default {
  components: {
    DModal,
    Loader,
    Tabs
  },
  data () {
    return {
      activeTab: 'select',
      isLoading: false,
      isSaving: false,
      organisations: [],
      selectedOrganisations: [],
      isLoadingOutside: false,
      outsideData: null,
      isShowAllOutside: false,
      isLoadingUnused: false,
      unusedEmployments: [],
      isUnusedLoaded: false,
      isConfirmingUnusedDelete: false,
      isDeletingUnused: false,
      theTabs: [
        {id: 'select', title: '1. Vælg organisationer'},
        {id: 'outside', title: '2. Overførsler uden for organisationer'},
        {id: 'unused', title: '3. Ubrugte ansættelser'}
      ]
    }
  },
  computed: {
    school () {
      return this.$store.state.activeSchool
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    isFullSize () {
      return this.activeTab !== 'select'
    },
    usersInHash () {
      return this.$store.getters.allUsersInHash
    },
    countsByOrganisation () {
      const hash = {}
      this.$store.state.employments.employments.forEach(employment => {
        if (employment.deleted) {
          return
        }
        const key = employment.organisation || ''
        if (!hash[key]) {
          hash[key] = {total: 0, withTemplate: 0}
        }
        hash[key].total++
        if (employment.employment_template_id != null && employment.employment_template_id !== '') {
          hash[key].withTemplate++
        }
      })
      return hash
    },
    // Ansættelser med sikre arrays og forberedte rækker: OPUS-engangsydelser
    // fladet ud til en linje pr. ydelse, og vikarløn-dage med tiden bogført på
    // netop denne ansættelses positioner.
    outsideEmployments () {
      if (!this.outsideData) {
        return []
      }
      return this.outsideData.employments.map(employment => {
        const safe = key => Array.isArray(employment[key]) ? employment[key] : []
        const positionIds = safe('positions').map(position => position.school_employment_id)

        const kmdRows = safe('kmd_engangsydelser').reduce((rows, record) => {
          const payload = Array.isArray(record.engangsydelser) ? record.engangsydelser : []
          payload.forEach(row => {
            rows.push({...row, v_status: record.status, v_created_at: record.created_at})
          })
          return rows
        }, [])

        const salaryDays = safe('salary_days').map(day => {
          const sums = Array.isArray(day.sums) ? day.sums : []
          const minutes = sums
            .filter(sum => sum && positionIds.indexOf(sum.employment_id * 1) > -1)
            .reduce((total, sum) => total + (sum.minutes * 1 || 0), 0)
          return {...day, v_minutes: minutes}
        })

        return {
          ...employment,
          salary_supplements: safe('salary_supplements'),
          absences: safe('absences'),
          sd_engangsydelser: safe('sd_engangsydelser'),
          sd_fravaer: safe('sd_fravaer'),
          sd_loenaendringer: safe('sd_loenaendringer'),
          sd_tillaeg: safe('sd_tillaeg'),
          v_kmd_rows: kmdRows,
          v_salary_days: salaryDays
        }
      })
    },
    employmentsWithTransfers () {
      return this.outsideEmployments.filter(employment => this.transfersCount(employment) > 0)
    },
    shownEmployments () {
      return this.isShowAllOutside ? this.outsideEmployments : this.employmentsWithTransfers
    }
  },
  methods: {
    transfersCount (employment) {
      return employment.salary_supplements.length +
        employment.absences.length +
        employment.sd_engangsydelser.length +
        employment.sd_fravaer.length +
        employment.sd_loenaendringer.length +
        employment.sd_tillaeg.length +
        employment.v_kmd_rows.length +
        employment.v_salary_days.length
    },
    setActiveTab (tabId) {
      this.activeTab = tabId
      if (tabId === 'outside') {
        this.readOutside()
      }
      if (tabId === 'unused') {
        this.readUnused()
      }
    },
    userName (userId) {
      const user = this.usersInHash[userId]
      return user && user.name ? user.name : 'Bruger ' + userId
    },
    readUnused () {
      this.isLoadingUnused = true
      this.isConfirmingUnusedDelete = false
      docendo.unusedEmployments(this.schoolId, data => {
        this.unusedEmployments = Array.isArray(data) ? data : []
        this.isUnusedLoaded = true
        this.isLoadingUnused = false
      }, failure => {
        console.log(failure)
        this.isLoadingUnused = false
        eventbus.toast = {text: 'Kunne ikke hente ubrugte ansættelser', success: false, position: 'bottomLeft'}
      })
    },
    deleteUnused () {
      if (this.isDeletingUnused) {
        return
      }
      this.isDeletingUnused = true
      docendo.deleteUnusedEmployments(this.schoolId, data => {
        const count = Array.isArray(data) ? data.length : 0
        this.isDeletingUnused = false
        this.isConfirmingUnusedDelete = false
        this.unusedEmployments = []
        eventbus.toast = {
          text: count + ' ' + (count === 1 ? 'ansættelse' : 'ansættelser') + ' slettet',
          success: true,
          position: 'bottomLeft'
        }
        // Siden bagved viser de samme ansættelser - hent dem igen uden de slettede.
        this.$store.dispatch('readEmployments')
      }, failure => {
        console.log(failure)
        this.isDeletingUnused = false
        this.isConfirmingUnusedDelete = false
        eventbus.toast = {text: 'Kunne ikke slette ansættelserne', success: false, position: 'bottomLeft'}
      })
    },
    formatDate (date) {
      return date ? moment(date).format('DD-MM-YYYY') : ''
    },
    formatPeriod (from, to) {
      if (!from && !to) {
        return ''
      }
      return this.formatDate(from) + ' - ' + this.formatDate(to)
    },
    // SD-engangsydelser bærer datoen som år/måned/dag i hver sin kolonne.
    formatSdDate (ydelse) {
      if (!ydelse.aar) {
        return ''
      }
      const pad = value => (value * 1 < 10 ? '0' : '') + value
      return pad(ydelse.dag) + '-' + pad(ydelse.maaned) + '-' + ydelse.aar
    },
    formatMinutes (minutes) {
      if (!minutes) {
        return ''
      }
      const hours = Math.floor(minutes / 60)
      const rest = Math.round(minutes % 60)
      return hours + 't ' + (rest < 10 ? '0' : '') + rest + 'm'
    },
    organisationCounts (organisation) {
      return this.countsByOrganisation[organisation || ''] || {total: 0, withTemplate: 0}
    },
    isSelected (organisation) {
      return this.selectedOrganisations.indexOf(organisation) > -1
    },
    toggleOrganisation (organisation) {
      const idx = this.selectedOrganisations.indexOf(organisation)
      if (idx > -1) {
        this.selectedOrganisations.splice(idx, 1)
      } else {
        this.selectedOrganisations.push(organisation)
      }
    },
    readOrganisations () {
      this.isLoading = true
      docendo.listSalaryOrganisations(this.schoolId, data => {
        this.organisations = data
        this.isLoading = false
      }, failure => {
        console.log(failure)
        this.isLoading = false
        eventbus.toast = {text: 'Kunne ikke hente organisationer', success: false, position: 'bottomLeft'}
      })
    },
    // Resultatet afhænger af den gemte liste, så der hentes friskt hver gang
    // fanen åbnes - og igen efter gem.
    readOutside () {
      this.isLoadingOutside = true
      // setTimeout: schools.update og dette kald deler korrelations-id i websocket-
      // klienten (payloadens id = skole-id). Affyres kaldet synkront fra gem-kaldets
      // success-callback, er id'et stadig optaget i klientens kø, og svaret her
      // matches så aldrig - spinneren hænger. Udskudt et tick er køen ryddet.
      setTimeout(() => {
        docendo.employmentsOutsideSalaryOrganisations(this.schoolId, data => {
          this.outsideData = data
          this.isLoadingOutside = false
        }, failure => {
          console.log(failure)
          this.isLoadingOutside = false
          eventbus.toast = {text: 'Kunne ikke hente overførsler', success: false, position: 'bottomLeft'}
        })
      }, 0)
    },
    save () {
      if (this.isLoading || this.isSaving) {
        return
      }
      this.isSaving = true
      docendo.updateSchool(this.schoolId, {salary_organisations: this.selectedOrganisations}, success => {
        this.isSaving = false
        this.$store.commit('setActiveSchool', {...this.school, salary_organisations: success.salary_organisations})
        eventbus.toast = {text: 'Gemt', success: true, position: 'bottomLeft'}
        // Videre til step 2, så fejlplacerede overførsler kan gennemgås med det samme.
        this.setActiveTab('outside')
      }, failure => {
        console.log(failure)
        this.isSaving = false
        eventbus.toast = {text: 'Kunne ikke gemme organisationer', success: false, position: 'bottomLeft'}
      })
    }
  },
  mounted () {
    const saved = this.school && this.school.salary_organisations
    this.selectedOrganisations = Array.isArray(saved) ? saved.slice() : []
    this.readOrganisations()
  }
}
</script>

<style scoped>
@import "../../../../src/assets/css/variables.css";

.organisations-modal {
  width: 460px;
  max-width: calc(100vw - 3rem);
  border-radius: 16px;
  position: relative;
}

.organisations-modal--full {
  width: calc(100vw - 6rem);
}

.organisations-modal__header {
  background: #fff;
  padding: 1rem;
  border-radius: 16px 16px 0 0;
  border-bottom: 1px solid #d8d8d8;
  position: relative;
}

.organisations-modal__header h2 {
  margin: 0;
}

.organisations-modal__close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 1.2rem;
  opacity: 0.85;
  cursor: pointer;
}

.organisations-modal__tabs {
  padding: 1rem 1rem 0 1rem;
}

.organisations-modal__content {
  padding: 1rem;
}

.organisations-modal__content--full {
  max-height: calc(100vh - 16rem);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.organisations-modal__explainer {
  margin: 0 0 1rem 0;
  font-style: italic;
  font-size: 11px;
  color: #8898aa;
}

.organisations-modal__warning {
  margin: 0 0 1rem 0;
  padding: 0.5rem 1rem;
  border: 1px solid #f5be58;
  border-radius: 4px;
  background: #fdf6e3;
  font-size: 12px;
}

.organisations-modal__list {
  max-height: 50vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: #fff;
}

.organisations-modal__list__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 0.5rem 1rem;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
}

.organisations-modal__list__item + .organisations-modal__list__item {
  border-top: 1px solid var(--border-color);
}

.organisations-modal__list__item:hover {
  background-color: #f6f9fc;
}

.organisations-modal__list__item--active {
  color: var(--primary);
  background-color: #f6f9fc;
}

.organisations-modal__list__item__name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.organisations-modal__list__item__counts {
  font-size: 11px;
  font-weight: normal;
  color: #8898aa;
}

.icon__checkmark {
  width: 30px;
  display: flex;
  height: 30px;
  align-items: center;
  justify-content: center;
}

.icon__checkmark span {
  font-size: 14px;
}

.organisations-modal__buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--border-color);
}

.organisations-modal__buttons > div {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.organisations-modal__count {
  font-style: italic;
  font-size: 11px;
}

.organisations-modal__outside-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 11px;
  font-style: italic;
}

.organisations-modal__outside-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  font-style: normal;
}

.outside-employment {
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: #fff;
  margin-bottom: 1rem;
}

.outside-employment__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f6f9fc;
  border-bottom: 1px solid var(--border-color);
  border-radius: 4px 4px 0 0;
}

.outside-employment__header__who {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.outside-employment__header__number {
  color: #8898aa;
  font-size: 11px;
}

.outside-employment__section {
  padding: 0.75rem 1rem;
}

.outside-employment__section + .outside-employment__section {
  border-top: 1px solid var(--border-color);
}

.outside-employment__section h4 {
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
}

.outside-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.outside-table th {
  text-align: left;
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  padding: 0.25rem 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.outside-table td {
  padding: 0.25rem 0.5rem;
  border-bottom: 1px solid #f0f3f7;
}

.outside-table tbody tr:last-child td {
  border-bottom: 0;
}

/* Fane 3-tabellen står direkte i indholdet, ikke i et ansættelseskort. */
.outside-table--boxed {
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: #fff;
}

.outside-table--boxed th {
  background: #f6f9fc;
}

.outside-table--boxed th,
.outside-table--boxed td {
  padding: 0.5rem;
}

.organisations-modal__no-transfers {
  padding: 0.75rem 1rem;
  margin: 0;
  color: #8898aa;
}

.d-tag {
  display: flex;
  align-items: center;
  padding: 1px 5px;
  border-radius: 4px;
  color: #333;
  border: 1px solid #c8d6e2;
  background: #fff;
  font-size: 11px;
  font-weight: 500;
}

.do-pill--small {
  padding: 0 0.5rem;
  font-size: 10px;
  line-height: 16px;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// SalaryOrganisationsModal.vue?de60b81a